import type { FlowSummaryDto } from '@/apis/backend/generated/models';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KLAVIYO_CONFIG } from '@/config/esp-config';
import { useOrganization } from '@/context/organization-context';
import { FlowHealthPanel } from '@/components/flows/FlowHealthPanel';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { TrialLockedPanel } from '@/sections/white-glove/trial-locked-panel';
import { useFlowControllerList } from '@/apis/backend/generated/flows/flows';
import { useWhiteGloveAccess } from '@/sections/white-glove/use-white-glove-access';
import { FEATURE_FLAGS, useFeatureFlag, useSelfServeSurface } from '@/hooks/useFeatureFlag';
import { useFlowInsightsControllerGetInsights } from '@/apis/backend/generated/flow-insights/flow-insights';
import {
  maxSeverity,
  severityMuiColor,
  FlowFindingsDrawer,
  groupFindingsByFlow,
} from '@/sections/flow-insights/flow-findings-drawer';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import Skeleton from '@mui/material/Skeleton';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import { WarningAmberRounded as WarningIcon } from '@mui/icons-material';

import { paths } from 'src/routes/paths';

import { DashboardContent } from 'src/layouts/dashboard';

import { Iconify } from 'src/components/iconify';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/flows/')({
  component: FlowsPage,
});

const STATUS_COLOR: Record<string, 'success' | 'default' | 'warning' | 'info'> = {
  live: 'success',
  draft: 'info',
  manual: 'warning',
  paused: 'warning',
  archived: 'default',
};

function pct(value?: number): string {
  if (value === undefined || value === null) return '--';
  return `${(value * 100).toFixed(1)}%`;
}

function money(value?: number): string {
  if (value === undefined || value === null) return '$0';
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

type SortColumn = 'name' | 'status' | 'triggerType' | 'steps' | 'openRate' | 'clickRate' | 'revenue';

function sortValue(f: FlowSummaryDto, col: SortColumn): string | number {
  switch (col) {
    case 'name': return (f.name ?? '').toLowerCase();
    case 'status': return f.status ?? '';
    case 'triggerType': return f.triggerType ?? '';
    case 'steps': return f.nexmailStepCount ?? 0;
    case 'openRate': return f.openRate ?? 0;
    case 'clickRate': return f.clickRate ?? 0;
    case 'revenue': return f.revenue ?? 0;
    default: return 0;
  }
}

function FlowsPage() {
  const { t } = useTranslation('flows');
  const navigate = useNavigate();
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const clientId = activeClient?.id ?? '';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortColumn>('revenue');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [findingsFlow, setFindingsFlow] = useState<{ name: string; flowId: string } | null>(null);

  // Flows is a self-serve surface, so it wants the DIY gate as well as its own
  // launch flag -- `flows` defaults to on, and reading it alone left the whole
  // planner reachable by URL for a brand that never bought it. Nothing about
  // flows is part of the managed service yet, so a managed brand meets the same
  // panel as a prospect; when that changes, this is the one place to say so.
  const flowsSelfServe = useSelfServeSurface(FEATURE_FLAGS.FLOWS);
  const { isLoading: accessLoading } = useWhiteGloveAccess();

  const { data, isLoading, error } = useFlowControllerList(orgId, clientId, {
    query: { enabled: !!orgId && !!clientId, staleTime: 60_000 },
  });

  // Insights findings (structural + performance), keyed by flow id, so each row
  // can flag setup/health issues → side panel. Rides the Flows feature flag.
  const insightsEnabled = useFeatureFlag(FEATURE_FLAGS.FLOWS);
  const { data: insights } = useFlowInsightsControllerGetInsights(orgId, clientId, undefined, {
    query: { enabled: insightsEnabled && !!orgId && !!clientId },
  });
  const findingsByFlow = useMemo(
    () => groupFindingsByFlow(insights?.findings ?? []).byFlow,
    [insights?.findings]
  );

  // Status values present in the data, for the filter chips.
  const availableStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const f of data?.flows ?? []) set.add(f.status);
    return Array.from(set);
  }, [data]);

  const flows = useMemo(() => {
    let all = data?.flows ?? [];
    const q = search.trim().toLowerCase();
    if (q) all = all.filter((f) => f.name.toLowerCase().includes(q));
    if (statusFilter !== 'all') all = all.filter((f) => f.status === statusFilter);
    const dir = sortOrder === 'asc' ? 1 : -1;
    return [...all].sort((a, b) => {
      const va = sortValue(a, sortBy);
      const vb = sortValue(b, sortBy);
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir;
      return ((va as number) - (vb as number)) * dir;
    });
  }, [data, search, statusFilter, sortBy, sortOrder]);

  const handleSort = (col: SortColumn) => {
    if (sortBy === col) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortOrder('desc'); }
  };

  // Waited on rather than guessed: drawing the planner and swapping it once the
  // tier lands would show a brand a product they do not have, however briefly.
  if (accessLoading) {
    return (
      <DashboardContent maxWidth="xl">
        <Stack alignItems="center" sx={{ py: 8 }}>
          <Skeleton variant="rounded" width="100%" height={240} />
        </Stack>
      </DashboardContent>
    );
  }

  if (!flowsSelfServe) {
    return (
      <TrialLockedPanel
        title={t('list.title')}
        body={t(
          'flows.locked',
          'Flows are the automated emails that send themselves -- welcome series, abandoned carts, win-backs.'
        )}
      />
    );
  }

  return (
    <DashboardContent maxWidth="xl">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">{t('list.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('list.subtitle')}
          </Typography>
        </Box>
        {KLAVIYO_CONFIG.flow?.supportsCreation && (
          <Button
            variant="contained"
            startIcon={<Iconify icon="solar:add-circle-linear" width={18} />}
            onClick={() => navigate({ to: paths.dashboard.flowCreate })}
          >
            {t('list.createFlow')}
          </Button>
        )}
      </Stack>

      <FlowHealthPanel />

      <Card sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder={t('list.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: <Iconify icon="solar:magnifer-linear" width={18} sx={{ mr: 1, color: 'text.disabled' }} />,
            }}
          />
          {availableStatuses.length > 1 && (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                label="All"
                size="small"
                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                color={statusFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setStatusFilter('all')}
              />
              {availableStatuses.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  variant={statusFilter === s ? 'filled' : 'outlined'}
                  color={statusFilter === s ? (STATUS_COLOR[s] ?? 'primary') : 'default'}
                  onClick={() => setStatusFilter(s)}
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Stack>
          )}
        </Stack>

        {!!error && (
          <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
            {t('list.loadError')}
          </Typography>
        )}

        {isLoading && (
          <Stack spacing={1} sx={{ py: 2 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height={48} />
            ))}
          </Stack>
        )}

        {!isLoading && !error && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {([
                    { id: 'name', label: t('list.columns.flow'), align: 'left' },
                    { id: 'status', label: t('list.columns.status'), align: 'left' },
                    { id: 'triggerType', label: t('list.columns.trigger'), align: 'left' },
                    { id: 'steps', label: t('list.columns.nexmailSteps'), align: 'right' },
                    { id: 'openRate', label: t('list.columns.open'), align: 'right' },
                    { id: 'clickRate', label: t('list.columns.click'), align: 'right' },
                    { id: 'revenue', label: t('list.columns.revenue'), align: 'right' },
                  ] as { id: SortColumn; label: string; align: 'left' | 'right' }[]).map((col) => (
                    <TableCell key={col.id} align={col.align}>
                      <TableSortLabel
                        active={sortBy === col.id}
                        direction={sortBy === col.id ? sortOrder : 'desc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {flows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        {t('list.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  flows.map((flow: FlowSummaryDto) => (
                    <TableRow
                      key={flow.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate({ to: paths.dashboard.flowDetail(flow.id) })}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {(() => {
                            const ff = findingsByFlow.get(flow.id);
                            if (!ff?.length) return null;
                            const sev = maxSeverity(ff) || 'info';
                            return (
                              <Tooltip title={`${ff.length} issue${ff.length > 1 ? 's' : ''} to review`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setFindingsFlow({ name: flow.name, flowId: flow.id }); }}
                                  sx={{ p: 0.25 }}
                                >
                                  <Badge badgeContent={ff.length} color={severityMuiColor(sev) as any}>
                                    <WarningIcon fontSize="small" color={severityMuiColor(sev) as any} />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                            );
                          })()}
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {flow.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={flow.status}
                          size="small"
                          color={STATUS_COLOR[flow.status] ?? 'default'}
                          variant={flow.status === 'live' ? 'filled' : 'outlined'}
                          sx={{ fontSize: 11, height: 20, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {flow.triggerType ?? '--'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {flow.nexmailStepCount} / {flow.emailStepCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{pct(flow.openRate)}</TableCell>
                      <TableCell align="right">{pct(flow.clickRate)}</TableCell>
                      <TableCell align="right">{money(flow.revenue)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <FlowFindingsDrawer
        open={!!findingsFlow}
        onClose={() => setFindingsFlow(null)}
        flowName={findingsFlow?.name ?? null}
        findings={findingsFlow ? findingsByFlow.get(findingsFlow.flowId) ?? [] : []}
        onOpenFlow={
          findingsFlow
            ? () => navigate({ to: paths.dashboard.flowDetail(findingsFlow.flowId) })
            : undefined
        }
      />
    </DashboardContent>
  );
}
